import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { CheckIcon } from 'lucide-react';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
  Button,
  MenuCloseIcon,
  type MultiSelectOption,
} from '@noor/ui';
import { cn } from '@noor/lib/cn';

interface MobileOptionPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Screen title, announced to assistive tech (the design shows no heading). */
  title: string;
  options: MultiSelectOption[];
  /** Values selected when the screen opens. */
  value: string[];
  /** Commits the picked set — fired by the footer button, not per tap. */
  onConfirm: (value: string[]) => void;
  /** Footer button label («Добавить»). */
  confirmLabel: string;
  closeLabel: string;
  /** Shown in place of the list when there is nothing to pick. */
  emptyText: string;
  /** Restrict the pick to ONE option (the branch field on an edit). */
  single?: boolean;
}

/**
 * The full-screen option picker the mobile promotion sheet opens for its
 * branch and product fields (Figma node 26280:95043).
 *
 * A separate screen rather than `MultiSelect`'s dropdown: below the shared
 * component's 1040px threshold it falls back to an anchored popover, which on
 * a phone would open a small list over the sheet that spawned it. The design
 * instead takes the whole screen — a close button, an undecorated divided list
 * of rows with a trailing checkbox, and a single «Добавить» that commits.
 *
 * Selection is LOCAL until confirmed, which is what the footer button is for:
 * the design has no per-tap commit and no cancel beyond the close button, so
 * dismissing must leave the field as it was.
 */
export function MobileOptionPicker({
  open,
  onOpenChange,
  title,
  options,
  value,
  onConfirm,
  confirmLabel,
  closeLabel,
  emptyText,
  single = false,
}: MobileOptionPickerProps) {
  // Seeded once per mount — callers mount this only while it is open (see
  // `PromotionModalMobile`), so reopening after a dismissal starts from what
  // the field actually holds rather than the abandoned draft, with no
  // re-seeding effect to cascade a render.
  const [picked, setPicked] = useState<string[]>(value);

  function toggle(optionValue: string): void {
    setPicked((current) => {
      if (single) return [optionValue];
      return current.includes(optionValue)
        ? current.filter((item) => item !== optionValue)
        : [...current, optionValue];
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-card" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-50 flex flex-col overflow-hidden bg-card',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* The design's frame carries no visible heading — just the floating
           * close button — but Radix still requires both for a11y. */}
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>

          <div className="flex shrink-0 items-center p-3">
            <DialogPrimitive.Close
              aria-label={closeLabel}
              className="flex size-10 cursor-pointer items-center justify-center rounded-3xl bg-card text-foreground shadow-float-halo transition-colors outline-none hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40 active:scale-95"
            >
              <MenuCloseIcon className="size-6" />
            </DialogPrimitive.Close>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-2 pb-4">
            {options.length === 0 ? (
              <p className="py-8 text-center text-sm tracking-tight text-muted-foreground">
                {emptyText}
              </p>
            ) : (
              <ul className="flex flex-col">
                {options.map((option, index) => {
                  const isPicked = picked.includes(option.value);
                  return (
                    <li key={option.value}>
                      {index > 0 && <div className="h-px w-full rounded-sm bg-border" />}
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={isPicked}
                        disabled={option.disabled ?? false}
                        onClick={() => toggle(option.value)}
                        className="flex w-full cursor-pointer items-center gap-2 py-4 text-left outline-none focus-visible:rounded-lg focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-base font-medium tracking-tight text-foreground">
                            {option.label}
                          </span>
                          {option.description ? (
                            <span className="mt-0.5 block truncate text-xs tracking-tight text-muted-foreground">
                              {option.description}
                            </span>
                          ) : null}
                        </span>
                        {/* Figma 6651:60513/60511 — a 24px box with an 8px
                         * radius: filled brand orange with a check when
                         * picked, a 2px gray outline when not. */}
                        <span
                          aria-hidden
                          className={cn(
                            'flex size-6 shrink-0 items-center justify-center rounded-[0.5rem] transition-colors',
                            isPicked
                              ? 'bg-primary text-primary-foreground'
                              : 'border-2 border-control-border',
                          )}
                        >
                          {isPicked && <CheckIcon className="size-4" strokeWidth={3} />}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="shrink-0 bg-card px-4 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-badge-halo">
            <Button
              type="button"
              size="lg"
              className="h-14 w-full rounded-full text-base"
              onClick={() => {
                onConfirm(picked);
                onOpenChange(false);
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
